import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Button, Icon } from '@ng-unifi/ui';
import {
  AssignCallboxTenantBody,
  CreateCallboxDeviceBody,
  CreateCallboxDirectoryMemberBody,
  CreateCallboxGroupBody,
  UpdateCallboxDeviceBody,
  UpdateCallboxDirectoryMemberBody,
  UpdateCallboxGroupBody,
  UpdateCallboxRingTargetBody,
} from '@ng-unifi/domain';
import { useAuth } from '../auth/AuthContext.js';
import { apiClient, ApiError, newIdempotencyKey } from '../lib/api.js';
import { fieldCls } from '../components/CrudTable.js';
import { CrudModal, FormField } from '../components/CrudModal.js';
import { LocalOnlyBanner } from '../components/LocalOnlyBanner.js';
import { RowActions, Toast } from '../components/RowActions.js';

type GroupKind = 'perimeter' | 'interior' | 'tenant' | 'private' | 'admin';
type Placement = 'perimeter' | 'interior';
type RingTargetType = 'tenant' | 'admin_office' | 'user' | 'custom' | 'none';
type MemberKind = 'tenant' | 'household' | 'admin_office' | 'custom';

interface TenantSummary {
  id: string;
  name: string;
  status: string;
  contact_email?: string | null;
}

interface UserSummary {
  id: string;
  name: string;
  email: string;
  role: string;
  tenant_id: string | null;
}

interface HardwareResource {
  id: string;
  unifi_resource_type: string;
  unifi_resource_id: string;
  unifi_device_id: string | null;
  name: string;
  full_name: string | null;
  metadata_json?: unknown;
  callbox_device?: { id: string; name: string } | null;
}

interface CallboxGroup {
  id: string;
  name: string;
  description: string | null;
  kind: GroupKind;
  owner_tenant_id: string | null;
  sync_status: string;
  owner_tenant?: TenantSummary | null;
}

interface CallboxAssignment {
  id: string;
  tenant_id: string;
  can_manage_directory: boolean;
  can_manage_ring_target: boolean;
  label_override: string | null;
  tenant?: TenantSummary;
}

interface CallboxDevice {
  id: string;
  building_resource_id: string | null;
  directory_group_id: string;
  unifi_device_id: string | null;
  name: string;
  location_label: string | null;
  placement: Placement;
  enabled: boolean;
  ring_target_type: RingTargetType;
  ring_tenant_id: string | null;
  ring_user_id: string | null;
  ring_label: string | null;
  ring_phone: string | null;
  notes: string | null;
  sync_status: string;
  last_sync_error?: string | null;
  last_synced_at?: string | null;
  directory_group?: CallboxGroup;
  building_resource?: HardwareResource | null;
  ring_tenant?: TenantSummary | null;
  ring_user?: UserSummary | null;
  tenant_assignments?: CallboxAssignment[];
}

interface DirectoryMember {
  id: string;
  directory_group_id: string;
  tenant_id: string | null;
  kind: MemberKind;
  display_name: string;
  subtitle: string | null;
  phone: string | null;
  visibility: string;
  target_tenant_id: string | null;
  source_household_id: string | null;
  sort_order: number;
  enabled: boolean;
  tenant?: TenantSummary | null;
  target_tenant?: TenantSummary | null;
}

interface ManagementData {
  groups: CallboxGroup[];
  devices: CallboxDevice[];
  resources: HardwareResource[];
  tenants: TenantSummary[];
  users: UserSummary[];
  members: DirectoryMember[];
}

interface TenantGroupBundle {
  group: CallboxGroup;
  devices: CallboxDevice[];
}

interface RingTargetSyncResponse {
  data: {
    synced: boolean;
    message: string;
    device: CallboxDevice;
  };
}

type ModalState =
  | { kind: 'group'; row: CallboxGroup | null }
  | { kind: 'device'; row: CallboxDevice | null }
  | { kind: 'link'; row: CallboxDevice }
  | { kind: 'assign'; row: CallboxDevice }
  | { kind: 'ring'; row: CallboxDevice }
  | { kind: 'member'; row: DirectoryMember | null }
  | { kind: 'delete-group'; row: CallboxGroup }
  | { kind: 'delete-device'; row: CallboxDevice }
  | { kind: 'delete-member'; row: DirectoryMember }
  | { kind: 'unassign'; row: CallboxDevice; assignment: CallboxAssignment };

const masterRequest = { skipTenantScope: true };

function errorMessage(err: unknown, fallback = 'Failed') {
  if (err instanceof ApiError || err instanceof Error) return err.message;
  return fallback;
}

function nullableText(value: string) {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function groupKindLabel(kind: GroupKind) {
  switch (kind) {
    case 'perimeter':
      return 'Perimeter';
    case 'interior':
      return 'Interior';
    case 'tenant':
      return 'Tenant';
    case 'private':
      return 'Private';
    case 'admin':
      return 'Admin';
  }
}

function memberKindLabel(kind: MemberKind) {
  switch (kind) {
    case 'tenant':
      return 'Tenant';
    case 'household':
      return 'Household';
    case 'admin_office':
      return 'Admin office';
    case 'custom':
      return 'Custom';
  }
}

function ringTargetLabel(device: CallboxDevice, tenantById: Map<string, TenantSummary>) {
  if (device.ring_target_type === 'tenant') {
    return (
      device.ring_tenant?.name ?? tenantById.get(device.ring_tenant_id ?? '')?.name ?? 'Tenant'
    );
  }
  if (device.ring_target_type === 'admin_office') return device.ring_label ?? 'Admin office';
  if (device.ring_target_type === 'user')
    return device.ring_user?.name ?? device.ring_label ?? 'Portal user';
  if (device.ring_target_type === 'custom')
    return device.ring_label ?? device.ring_phone ?? 'Custom';
  return 'None';
}

function syncStatusLabel(status: string) {
  switch (status) {
    case 'synced':
      return 'Synced';
    case 'pending':
      return 'Pending';
    case 'failed':
      return 'Failed';
    case 'unverified':
      return 'Unverified';
    case 'local_only':
      return 'Local only';
    default:
      return status.replace(/_/g, ' ');
  }
}

function syncStatusClass(status: string) {
  switch (status) {
    case 'synced':
      return 'bg-secondary-container text-on-secondary-container';
    case 'failed':
      return 'bg-error-container text-on-error-container';
    case 'pending':
    case 'unverified':
      return 'bg-primary-container text-on-primary-container';
    default:
      return 'bg-surface-container-high text-on-surface-variant';
  }
}

function userOptionLabel(user: UserSummary) {
  return `${user.name} - ${user.email}`;
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}

function stringMeta(metadata: Record<string, unknown>, keys: string[]): string | null {
  const allowedKeys = new Set(keys);
  for (const [key, value] of Object.entries(metadata)) {
    if (!allowedKeys.has(key)) continue;
    if (typeof value === 'string' && value.trim().length > 0) return value.trim();
  }
  return null;
}

function compactHardwareName(value: string | null | undefined): string {
  const trimmed = value?.trim();
  if (!trimmed) return '';
  if (trimmed.includes(' - ')) {
    const parts = trimmed.split(' - ').map((part) => part.trim()).filter(Boolean);
    if (parts.length >= 3) return parts.slice(2).join(' - ');
  }
  if (trimmed.includes('/')) {
    const parts = trimmed.split('/').map((part) => part.trim()).filter(Boolean);
    return parts.at(-1) ?? trimmed;
  }
  return trimmed;
}

function hardwareDisplayName(resource: HardwareResource) {
  return compactHardwareName(resource.full_name ?? resource.name) || resource.name;
}

function resourceOptionLabel(resource: HardwareResource) {
  const metadata = asRecord(resource.metadata_json);
  const status = stringMeta(metadata, ['status', 'state', 'connection_state']);
  const parts = [hardwareDisplayName(resource), status ? status.toLowerCase() : null].filter(Boolean);
  return parts.join(' - ');
}

function resourceDefaultName(resource: HardwareResource) {
  return hardwareDisplayName(resource);
}

function callboxLocationLabel(device: CallboxDevice) {
  const resourceName = compactHardwareName(
    device.building_resource?.full_name ?? device.building_resource?.name,
  );
  return device.location_label ?? (resourceName || 'Hardware not linked');
}

function callboxSemanticName(device: CallboxDevice) {
  return (
    device.name ||
    compactHardwareName(device.building_resource?.full_name ?? device.building_resource?.name) ||
    'Callbox'
  );
}

function callboxSecondaryLabel(device: CallboxDevice) {
  const location = callboxLocationLabel(device);
  const name = callboxSemanticName(device);
  return location !== name ? location : null;
}

function linkedCallboxLabel(count: number) {
  if (count > 1) {
    const otherCount = count - 1;
    return `Linked with ${otherCount} other callbox${otherCount === 1 ? '' : 'es'}`;
  }
  return 'Not linked to other callboxes';
}

function groupDeviceCountLabel(count: number) {
  if (count === 0) return 'No callboxes';
  if (count === 1) return '1 callbox';
  return `${count} linked callboxes`;
}

function tenantCanManageRingTarget(device: CallboxDevice) {
  return (device.tenant_assignments ?? []).some((assignment) => assignment.can_manage_ring_target);
}

function tenantCanManageDirectory(device: CallboxDevice) {
  return (device.tenant_assignments ?? []).some((assignment) => assignment.can_manage_directory);
}

function Pill({ children, muted = false }: { children: React.ReactNode; muted?: boolean }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-1 font-label-md text-label-md ${
        muted
          ? 'bg-surface-container-high text-on-surface-variant'
          : 'bg-secondary-container text-on-secondary-container'
      }`}
    >
      {children}
    </span>
  );
}

export function CallboxDirectoryPage() {
  const auth = useAuth();
  const isMaster = auth.state.status === 'authed' && auth.state.user.role === 'master_admin';
  const [management, setManagement] = useState<ManagementData | null>(null);
  const [tenantGroups, setTenantGroups] = useState<TenantGroupBundle[]>([]);
  const [tenantMembers, setTenantMembers] = useState<DirectoryMember[]>([]);
  const [tenantUsers, setTenantUsers] = useState<UserSummary[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [refreshingHardware, setRefreshingHardware] = useState(false);
  const [syncingDeviceId, setSyncingDeviceId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (auth.state.status !== 'authed') return;
    setLoading(true);
    setError(null);
    try {
      if (isMaster) {
        const res = await apiClient.get<{ data: ManagementData }>(
          '/api/v1/admin/callboxes',
          masterRequest,
        );
        setManagement(res.data);
        setTenantGroups([]);
        setTenantMembers([]);
        setTenantUsers([]);
      } else {
        const [groupsRes, membersRes, optionsRes] = await Promise.all([
          apiClient.get<{ data: TenantGroupBundle[] }>('/api/v1/callbox/groups'),
          apiClient.get<{ data: DirectoryMember[] }>('/api/v1/callbox/members'),
          apiClient.get<{ data: { users: UserSummary[] } }>('/api/v1/callbox/ring-target-options'),
        ]);
        setManagement(null);
        setTenantGroups(groupsRes.data);
        setTenantMembers(membersRes.data);
        setTenantUsers(optionsRes.data.users);
      }
    } catch (err) {
      setError(errorMessage(err, 'Failed to load callbox records'));
    } finally {
      setLoading(false);
    }
  }, [auth.state.status, isMaster]);

  useEffect(() => {
    void load();
  }, [load, reloadTick]);

  const groups = useMemo(
    () => (isMaster ? (management?.groups ?? []) : tenantGroups.map((g) => g.group)),
    [isMaster, management?.groups, tenantGroups],
  );
  const groupIdsKey = groups.map((g) => g.id).join('|');

  useEffect(() => {
    if (!selectedGroupId || !groups.some((g) => g.id === selectedGroupId)) {
      setSelectedGroupId(groups[0]?.id ?? '');
    }
  }, [groupIdsKey, groups, selectedGroupId]);

  const selectedGroup = groups.find((g) => g.id === selectedGroupId) ?? null;
  const tenants = management?.tenants ?? [];
  const users = isMaster ? (management?.users ?? []) : tenantUsers;
  const tenantById = useMemo(() => new Map(tenants.map((t) => [t.id, t])), [tenants]);
  const devices = isMaster
    ? (management?.devices ?? []).filter((d) => d.directory_group_id === selectedGroupId)
    : (tenantGroups.find((g) => g.group.id === selectedGroupId)?.devices ?? []);
  const members = isMaster
    ? (management?.members ?? []).filter((m) => m.directory_group_id === selectedGroupId)
    : tenantMembers.filter((m) => m.directory_group_id === selectedGroupId);
  const deviceCountByGroup = useMemo(() => {
    const counts = new Map<string, number>();
    const allDevices = isMaster
      ? (management?.devices ?? [])
      : tenantGroups.flatMap((bundle) => bundle.devices);
    for (const device of allDevices) {
      counts.set(device.directory_group_id, (counts.get(device.directory_group_id) ?? 0) + 1);
    }
    return counts;
  }, [isMaster, management?.devices, tenantGroups]);
  const memberCountByGroup = useMemo(() => {
    const counts = new Map<string, number>();
    const allMembers = isMaster ? (management?.members ?? []) : tenantMembers;
    for (const member of allMembers) {
      counts.set(member.directory_group_id, (counts.get(member.directory_group_id) ?? 0) + 1);
    }
    return counts;
  }, [isMaster, management?.members, tenantMembers]);
  const canCreateDirectoryEntry = isMaster || devices.some(tenantCanManageDirectory);

  const reload = useCallback(() => setReloadTick((t) => t + 1), []);
  const refreshHardware = useCallback(async () => {
    if (!isMaster) return;
    setRefreshingHardware(true);
    setError(null);
    try {
      await apiClient.post('/api/v1/admin/hardware/resources/refresh', {}, masterRequest);
      setToast('Hardware inventory refreshed.');
      reload();
    } catch (err) {
      setError(errorMessage(err, 'Failed to refresh hardware inventory'));
    } finally {
      setRefreshingHardware(false);
    }
  }, [isMaster, reload]);
  // UX-013: one key per device per sync intent, so retrying a failed sync is
  // the same operation rather than a second one.
  const syncKeysRef = useRef<Map<string, string>>(new Map());
  const syncRingTarget = useCallback(
    async (device: CallboxDevice) => {
      if (!isMaster) return;
      setSyncingDeviceId(device.id);
      setError(null);
      let key = syncKeysRef.current.get(device.id);
      if (!key) {
        key = newIdempotencyKey();
        syncKeysRef.current.set(device.id, key);
      }
      try {
        const res = await apiClient
          .withIdempotencyKey(key)
          .post<RingTargetSyncResponse>(
            `/api/v1/admin/callboxes/devices/${device.id}/ring-target/sync`,
            {},
            masterRequest,
          );
        if (!res.data.synced) {
          throw new Error(res.data.message || 'Call routing sync failed.');
        }
        // UX-011: reports what the server said rather than asserting a
        // controller outcome the compatibility matrix does not cover.
        setToast(res.data.message || `Button routing for "${device.name}" pushed to the callbox.`);
        syncKeysRef.current.delete(device.id);
        reload();
      } catch (err) {
        setError(errorMessage(err, 'Failed to sync button routing'));
      } finally {
        setSyncingDeviceId(null);
      }
    },
    [isMaster, reload],
  );
  const done = useCallback(
    (message: string) => {
      setModal(null);
      setToast(message);
      reload();
    },
    [reload],
  );

  if (auth.state.status === 'loading') {
    return (
      <div role="status" className="p-6 text-on-surface-variant">
        Loading callboxes...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex items-center gap-4">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-primary-container text-on-primary">
            <Icon name="contact_phone" className="text-2xl" />
          </div>
          <div>
            <h1 className="font-headline-md text-headline-md text-on-surface">Callboxes</h1>
            {/* UX-011: this claimed the page could "publish button routing to
                UniFi". The compatibility matrix lists callbox/caller button
                routing under Not Yet Implemented — the official developer API
                does not expose it, and the only observed path is a private
                web-UI contract deliberately left unverified. */}
            <p className="font-body-md text-body-md text-on-surface-variant">
              {isMaster
                ? 'Link callboxes into shared directory sets and manage button routing for them.'
                : 'Manage directory entries and delegated button routing for assigned callboxes.'}
            </p>
          </div>
        </div>
        {isMaster ? (
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={refreshHardware} disabled={refreshingHardware}>
              <Icon name={refreshingHardware ? 'hourglass_empty' : 'sync'} />
              <span>{refreshingHardware ? 'Refreshing' : 'Refresh hardware'}</span>
            </Button>
            <Button onClick={() => setModal({ kind: 'group', row: null })}>
              <Icon name="add" />
              <span>New linked set</span>
            </Button>
            <Button
              variant="secondary"
              onClick={() => setModal({ kind: 'device', row: null })}
              disabled={groups.length === 0}
            >
              <Icon name="add_home_work" />
              <span>Add callbox</span>
            </Button>
          </div>
        ) : null}
      </header>

      {/* UX-011: LocalOnlyBanner's own doc comment claimed it was reused here.
          It was not — the one page whose writes do not reach hardware was the
          only page without the label. Callbox/caller button routing is listed
          under "Not Yet Implemented" in the UniFi compatibility matrix. */}
      <LocalOnlyBanner
        title="Portal-managed directory"
        body="Directory entries and button routing are recorded in the portal. UniFi does not publish an official callbox routing API yet, so these settings are not enforced by the controller until that contract is verified."
      />

      {toast ? <Toast message={toast} onDismiss={() => setToast(null)} /> : null}

      {error ? (
        <div
          role="alert"
          className="rounded-lg bg-error-container px-4 py-3 text-on-error-container"
        >
          {error}
        </div>
      ) : null}

      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-title-lg text-title-lg text-on-surface">Linked sets</h2>
            <p className="font-body-sm text-body-sm text-on-surface-variant">
              {isMaster
                ? 'Callboxes in the same set share the same visible directory.'
                : 'Assigned callboxes share the visible directory for this set.'}
            </p>
          </div>
          {isMaster && selectedGroup ? (
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setModal({ kind: 'group', row: selectedGroup })}
              >
                <Icon name="edit" />
                <span>Edit set</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setModal({ kind: 'delete-group', row: selectedGroup })}
              >
                <Icon name="delete" />
                <span>Delete</span>
              </Button>
            </div>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-2">
          {groups.map((group) => (
            <button
              key={group.id}
              type="button"
              onClick={() => setSelectedGroupId(group.id)}
              aria-label={`${group.name}, ${groupKindLabel(group.kind)}, ${groupDeviceCountLabel(
                deviceCountByGroup.get(group.id) ?? 0,
              )}`}
              className={`min-w-48 rounded-lg border px-3 py-2 text-left font-label-lg text-label-lg ${
                group.id === selectedGroupId
                  ? 'border-primary bg-primary-container text-on-primary-container'
                  : 'border-outline-variant bg-surface text-on-surface'
              }`}
            >
              <span className="block">{group.name}</span>
              <span className="block font-body-sm text-body-sm opacity-75">
                {groupDeviceCountLabel(deviceCountByGroup.get(group.id) ?? 0)} ·{' '}
                {memberCountByGroup.get(group.id) ?? 0} entries
              </span>
            </button>
          ))}
        </div>
        {!loading && groups.length === 0 ? (
          <div className="rounded-lg border border-outline-variant bg-surface px-4 py-5 text-on-surface-variant">
            {isMaster
              ? 'Create a linked set before registering callboxes.'
              : 'No callbox directories are assigned.'}
          </div>
        ) : null}
      </section>

      {selectedGroup ? (
        <>
          <section className="space-y-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="font-title-lg text-title-lg text-on-surface">Callboxes</h2>
                <p className="font-body-sm text-body-sm text-on-surface-variant">
                  {selectedGroup.name} · {groupDeviceCountLabel(devices.length)}
                </p>
              </div>
              {isMaster ? (
                <Button size="sm" onClick={() => setModal({ kind: 'device', row: null })}>
                  <Icon name="add" />
                  <span>Add callbox</span>
                </Button>
              ) : null}
            </div>
            <CallboxDeviceTable
              devices={devices}
              selectedGroup={selectedGroup}
              isMaster={isMaster}
              tenantById={tenantById}
              onEdit={(row) => setModal({ kind: 'device', row })}
              onLink={(row) => setModal({ kind: 'link', row })}
              onEditRing={(row) => setModal({ kind: 'ring', row })}
              onSyncRing={(row) => void syncRingTarget(row)}
              onAssign={(row) => setModal({ kind: 'assign', row })}
              onDelete={(row) => setModal({ kind: 'delete-device', row })}
              onUnassign={(row, assignment) => setModal({ kind: 'unassign', row, assignment })}
              syncingDeviceId={syncingDeviceId}
            />
          </section>

          <section className="space-y-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="font-title-lg text-title-lg text-on-surface">Directory entries</h2>
                <p className="font-body-sm text-body-sm text-on-surface-variant">
                  {members.length} active portal listing{members.length === 1 ? '' : 's'}
                </p>
              </div>
              {canCreateDirectoryEntry ? (
                <Button size="sm" onClick={() => setModal({ kind: 'member', row: null })}>
                  <Icon name="add" />
                  <span>Entry</span>
                </Button>
              ) : null}
            </div>
            <DirectoryMemberTable
              members={members}
              isMaster={isMaster}
              onEdit={(row) => setModal({ kind: 'member', row })}
              onDelete={(row) => setModal({ kind: 'delete-member', row })}
            />
          </section>
        </>
      ) : null}

      {modal?.kind === 'group' && isMaster ? (
        <GroupModal
          row={modal.row}
          tenants={tenants}
          onCancel={() => setModal(null)}
          onDone={done}
        />
      ) : null}
      {modal?.kind === 'device' && isMaster ? (
        <DeviceModal
          row={modal.row}
          groups={groups}
          selectedGroupId={selectedGroupId}
          resources={management?.resources ?? []}
          tenants={tenants}
          users={users}
          onCancel={() => setModal(null)}
          onDone={done}
        />
      ) : null}
      {modal?.kind === 'link' && isMaster ? (
        <LinkCallboxModal
          row={modal.row}
          groups={groups}
          onCancel={() => setModal(null)}
          onDone={done}
        />
      ) : null}
      {modal?.kind === 'assign' && isMaster ? (
        <AssignTenantModal
          row={modal.row}
          tenants={tenants}
          onCancel={() => setModal(null)}
          onDone={done}
        />
      ) : null}
      {modal?.kind === 'ring' && !isMaster ? (
        <RingTargetModal
          row={modal.row}
          users={users}
          tenantId={auth.state.status === 'authed' ? (auth.state.user.tenant_id ?? null) : null}
          onCancel={() => setModal(null)}
          onDone={done}
        />
      ) : null}
      {modal?.kind === 'member' ? (
        <MemberModal
          row={modal.row}
          isMaster={isMaster}
          groups={groups}
          tenants={tenants}
          selectedGroupId={selectedGroupId}
          onCancel={() => setModal(null)}
          onDone={done}
        />
      ) : null}
      {modal?.kind === 'delete-group' && isMaster ? (
        <DeleteModal
          title={`Delete ${modal.row.name}`}
          body="Directory entries are removed with this linked set. Move registered callboxes first."
          submitLabel="Delete"
          onCancel={() => setModal(null)}
          onSubmit={async () => {
            await apiClient.delete(`/api/v1/admin/callboxes/groups/${modal.row.id}`, masterRequest);
            done(`Linked set "${modal.row.name}" deleted.`);
          }}
        />
      ) : null}
      {modal?.kind === 'delete-device' && isMaster ? (
        <DeleteModal
          title={`Remove ${modal.row.name}`}
          body="The callbox record and tenant assignments will be removed from the portal."
          submitLabel="Remove"
          onCancel={() => setModal(null)}
          onSubmit={async () => {
            await apiClient.delete(
              `/api/v1/admin/callboxes/devices/${modal.row.id}`,
              masterRequest,
            );
            done(`Callbox "${modal.row.name}" removed.`);
          }}
        />
      ) : null}
      {modal?.kind === 'delete-member' ? (
        <DeleteModal
          title={`Delete ${modal.row.display_name}`}
          body="The entry will be removed from this portal directory."
          submitLabel="Delete"
          onCancel={() => setModal(null)}
          onSubmit={async () => {
            const url = isMaster
              ? `/api/v1/admin/callboxes/directory/${modal.row.id}`
              : `/api/v1/callbox/members/${modal.row.id}`;
            await apiClient.delete(url, isMaster ? masterRequest : undefined);
            done(`Entry "${modal.row.display_name}" deleted.`);
          }}
        />
      ) : null}
      {modal?.kind === 'unassign' && isMaster ? (
        <DeleteModal
          title={`Unassign ${modal.assignment.tenant?.name ?? 'tenant'}`}
          body="The tenant will lose management access to this callbox directory."
          submitLabel="Unassign"
          onCancel={() => setModal(null)}
          onSubmit={async () => {
            await apiClient.delete(
              `/api/v1/admin/callboxes/devices/${modal.row.id}/tenants/${modal.assignment.tenant_id}`,
              masterRequest,
            );
            done('Callbox assignment removed.');
          }}
        />
      ) : null}
    </div>
  );
}

function CallboxDeviceTable({
  devices,
  selectedGroup,
  isMaster,
  tenantById,
  onEdit,
  onLink,
  onEditRing,
  onSyncRing,
  onAssign,
  onDelete,
  onUnassign,
  syncingDeviceId,
}: {
  devices: CallboxDevice[];
  selectedGroup: CallboxGroup;
  isMaster: boolean;
  tenantById: Map<string, TenantSummary>;
  onEdit: (row: CallboxDevice) => void;
  onLink: (row: CallboxDevice) => void;
  onEditRing: (row: CallboxDevice) => void;
  onSyncRing: (row: CallboxDevice) => void;
  onAssign: (row: CallboxDevice) => void;
  onDelete: (row: CallboxDevice) => void;
  onUnassign: (row: CallboxDevice, assignment: CallboxAssignment) => void;
  syncingDeviceId: string | null;
}) {
  if (devices.length === 0) {
    return (
      <div className="rounded-lg border border-outline-variant bg-surface px-4 py-5 text-on-surface-variant">
        No callboxes are linked to this set.
      </div>
    );
  }
  const showActions = isMaster || devices.some(tenantCanManageRingTarget);
  return (
    <div className="grid gap-3">
      {devices.map((device) => {
        const secondaryLabel = callboxSecondaryLabel(device);
        const linkedLabel = linkedCallboxLabel(devices.length);
        const statusLabel = syncStatusLabel(device.sync_status);
        const syncDisabled = syncingDeviceId !== null || device.ring_target_type === 'none';
        return (
          <article
            key={device.id}
            className="rounded-lg border border-outline-variant bg-surface px-4 py-4 text-on-surface"
          >
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0 space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-surface-container text-on-surface-variant">
                    <Icon name="door_front" className="text-xl" />
                  </span>
                  <div className="min-w-0">
                    <h3 className="truncate font-title-md text-title-md">
                      {callboxSemanticName(device)}
                    </h3>
                    {secondaryLabel ? (
                      <p className="truncate font-body-sm text-body-sm text-on-surface-variant">
                        {secondaryLabel}
                      </p>
                    ) : null}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <span
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-1 font-label-md text-label-md ${syncStatusClass(
                      device.sync_status,
                    )}`}
                  >
                    <Icon
                      name={device.sync_status === 'failed' ? 'error' : 'sync'}
                      className="text-base"
                    />
                    {statusLabel}
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-surface-container-high px-2 py-1 font-label-md text-label-md text-on-surface-variant">
                    <Icon name={devices.length > 1 ? 'hub' : 'link_off'} className="text-base" />
                    {linkedLabel}
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-surface-container-high px-2 py-1 font-label-md text-label-md text-on-surface-variant">
                    <Icon name="folder_shared" className="text-base" />
                    {selectedGroup.name}
                  </span>
                </div>
                <p className="font-body-sm text-body-sm text-on-surface-variant">
                  Button routes to {ringTargetLabel(device, tenantById)}.
                  {device.last_sync_error ? ` ${device.last_sync_error}` : ''}
                </p>
              </div>
              {showActions ? (
                <div className="flex flex-wrap items-center gap-2 lg:justify-end">
                  {isMaster ? (
                    <>
                      <Button size="sm" variant="secondary" onClick={() => onLink(device)}>
                        <Icon name="hub" />
                        <span>Link / move</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => onSyncRing(device)}
                        disabled={syncDisabled}
                        loading={syncingDeviceId === device.id}
                      >
                        <Icon name="sync" />
                        <span>{syncingDeviceId === device.id ? 'Syncing' : 'Sync'}</span>
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => onEdit(device)}>
                        <Icon name="edit" />
                        <span>Edit</span>
                      </Button>
                      <RowActions
                        actions={[
                          { label: 'Assign tenant', onClick: () => onAssign(device) },
                          { label: 'Delete', destructive: true, onClick: () => onDelete(device) },
                          ...(device.tenant_assignments ?? []).map((assignment) => ({
                            label: `Unassign ${assignment.tenant?.name ?? 'tenant'}`,
                            destructive: true,
                            onClick: () => onUnassign(device, assignment),
                          })),
                        ]}
                      />
                    </>
                  ) : tenantCanManageRingTarget(device) ? (
                    <Button size="sm" variant="secondary" onClick={() => onEditRing(device)}>
                      <Icon name="notifications_active" />
                      <span>Button routing</span>
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </div>
          </article>
        );
      })}
    </div>
  );
}

function DirectoryMemberTable({
  members,
  isMaster,
  onEdit,
  onDelete,
}: {
  members: DirectoryMember[];
  isMaster: boolean;
  onEdit: (row: DirectoryMember) => void;
  onDelete: (row: DirectoryMember) => void;
}) {
  if (members.length === 0) {
    return (
      <div className="rounded-lg border border-outline-variant bg-surface px-4 py-5 text-on-surface-variant">
        No directory entries.
      </div>
    );
  }
  return (
    <div className="grid gap-2">
      {members.map((member) => {
        const owner =
          member.tenant?.name ?? member.target_tenant?.name ?? (isMaster ? 'Building' : 'Tenant');
        return (
          <article
            key={member.id}
            className="flex flex-col gap-3 rounded-lg border border-outline-variant bg-surface px-4 py-3 text-on-surface sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-body-md text-body-md font-medium">{member.display_name}</h3>
                <Pill muted={!member.enabled}>
                  {member.enabled ? member.visibility : 'disabled'}
                </Pill>
              </div>
              <p className="mt-1 font-body-sm text-body-sm text-on-surface-variant">
                {memberKindLabel(member.kind)} entry · {owner}
                {member.subtitle ? ` · ${member.subtitle}` : ''}
                {member.phone ? ` · ${member.phone}` : ''}
              </p>
            </div>
            <RowActions
              actions={[
                { label: 'Edit entry', onClick: () => onEdit(member) },
                { label: 'Delete entry', destructive: true, onClick: () => onDelete(member) },
              ]}
            />
          </article>
        );
      })}
    </div>
  );
}

function GroupModal({
  row,
  tenants,
  onCancel,
  onDone,
}: {
  row: CallboxGroup | null;
  tenants: TenantSummary[];
  onCancel: () => void;
  onDone: (message: string) => void;
}) {
  const [name, setName] = useState(row?.name ?? '');
  const [description, setDescription] = useState(row?.description ?? '');
  const [kind, setKind] = useState<GroupKind>(row?.kind ?? 'perimeter');
  const [ownerTenantId, setOwnerTenantId] = useState(row?.owner_tenant_id ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const idemKey = useMemo(() => newIdempotencyKey(), []);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      if (row) {
        const body = UpdateCallboxGroupBody.parse({
          name: name.trim(),
          description: nullableText(description),
          kind,
          owner_tenant_id: ownerTenantId || null,
        });
        await apiClient
          .withIdempotencyKey(idemKey)
          .patch(`/api/v1/admin/callboxes/groups/${row.id}`, body, masterRequest);
        onDone(`Linked set "${body.name}" updated.`);
      } else {
        const body = CreateCallboxGroupBody.parse({
          name: name.trim(),
          description: nullableText(description),
          kind,
          owner_tenant_id: ownerTenantId || null,
        });
        await apiClient
          .withIdempotencyKey(idemKey)
          .post('/api/v1/admin/callboxes/groups', body, masterRequest);
        onDone(`Linked set "${body.name}" created.`);
      }
    } catch (err) {
      setError(errorMessage(err));
      setSubmitting(false);
    }
  }

  return (
    <CrudModal
      title={row ? `Edit ${row.name}` : 'Create linked set'}
      icon={row ? 'edit' : 'add'}
      submitting={submitting}
      error={error}
      submitLabel={row ? 'Save' : 'Create'}
      canSubmit={name.trim().length > 0}
      onCancel={onCancel}
      onSubmit={onSubmit}
    >
      <FormField id="callbox-group-name" label="Name">
        <input
          id="callbox-group-name"
          className={fieldCls}
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={120}
          required
        />
      </FormField>
      <FormField id="callbox-group-kind" label="Type">
        <select
          id="callbox-group-kind"
          className={fieldCls}
          value={kind}
          onChange={(e) => setKind(e.target.value as GroupKind)}
        >
          <option value="perimeter">Perimeter</option>
          <option value="interior">Interior</option>
          <option value="tenant">Tenant</option>
          <option value="private">Private</option>
          <option value="admin">Admin</option>
        </select>
      </FormField>
      <FormField id="callbox-group-owner" label="Owner tenant">
        <select
          id="callbox-group-owner"
          className={fieldCls}
          value={ownerTenantId}
          onChange={(e) => setOwnerTenantId(e.target.value)}
        >
          <option value="">Building</option>
          {tenants.map((tenant) => (
            <option key={tenant.id} value={tenant.id}>
              {tenant.name}
            </option>
          ))}
        </select>
      </FormField>
      <FormField id="callbox-group-description" label="Description">
        <textarea
          id="callbox-group-description"
          className={fieldCls}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={500}
          rows={3}
        />
      </FormField>
    </CrudModal>
  );
}

function DeviceModal({
  row,
  groups,
  selectedGroupId,
  resources,
  tenants,
  users,
  onCancel,
  onDone,
}: {
  row: CallboxDevice | null;
  groups: CallboxGroup[];
  selectedGroupId: string;
  resources: HardwareResource[];
  tenants: TenantSummary[];
  users: UserSummary[];
  onCancel: () => void;
  onDone: (message: string) => void;
}) {
  const [directoryGroupId, setDirectoryGroupId] = useState(
    row?.directory_group_id ?? selectedGroupId,
  );
  const [resourceId, setResourceId] = useState(row?.building_resource_id ?? '');
  const [name, setName] = useState(row?.name ?? '');
  const [locationLabel, setLocationLabel] = useState(row?.location_label ?? '');
  const [placement, setPlacement] = useState<Placement>(row?.placement ?? 'perimeter');
  const [enabled, setEnabled] = useState(row?.enabled ?? true);
  const [ringTargetType, setRingTargetType] = useState<RingTargetType>(
    row?.ring_target_type ?? 'admin_office',
  );
  const [ringTenantId, setRingTenantId] = useState(row?.ring_tenant_id ?? '');
  const [ringUserId, setRingUserId] = useState(row?.ring_user_id ?? '');
  const [ringLabel, setRingLabel] = useState(row?.ring_label ?? '');
  const [ringPhone, setRingPhone] = useState(row?.ring_phone ?? '');
  const [notes, setNotes] = useState(row?.notes ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const idemKey = useMemo(() => newIdempotencyKey(), []);
  const availableResources = resources.filter(
    (r) => !r.callbox_device || r.callbox_device.id === row?.id,
  );
  const selectedResource =
    availableResources.find((resource) => resource.id === resourceId) ?? null;

  function onResourceChange(nextResourceId: string) {
    setResourceId(nextResourceId);
    const resource = availableResources.find((r) => r.id === nextResourceId);
    if (!resource) return;
    const label = resourceDefaultName(resource);
    if (!row && name.trim().length === 0) setName(label);
    if (!row && locationLabel.trim().length === 0) setLocationLabel(label);
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        building_resource_id: resourceId,
        directory_group_id: directoryGroupId,
        name: name.trim(),
        location_label: nullableText(locationLabel),
        placement,
        enabled,
        ring_target_type: ringTargetType,
        ring_tenant_id: ringTargetType === 'tenant' ? ringTenantId || null : null,
        ring_user_id: ringTargetType === 'user' ? ringUserId || null : null,
        ring_label: nullableText(ringLabel),
        ring_phone: nullableText(ringPhone),
        notes: nullableText(notes),
      };
      if (row) {
        const body = UpdateCallboxDeviceBody.parse(payload);
        await apiClient
          .withIdempotencyKey(idemKey)
          .patch(`/api/v1/admin/callboxes/devices/${row.id}`, body, masterRequest);
        onDone(`Callbox "${body.name ?? row.name}" updated.`);
      } else {
        const body = CreateCallboxDeviceBody.parse(payload);
        await apiClient
          .withIdempotencyKey(idemKey)
          .post('/api/v1/admin/callboxes/devices', body, masterRequest);
        onDone(`Callbox "${body.name}" registered.`);
      }
    } catch (err) {
      setError(errorMessage(err));
      setSubmitting(false);
    }
  }

  return (
    <CrudModal
      title={row ? `Edit ${row.name}` : 'Register callbox'}
      icon={row ? 'edit' : 'add_home_work'}
      submitting={submitting}
      error={error}
      submitLabel={row ? 'Save' : 'Register'}
      canSubmit={name.trim().length > 0 && directoryGroupId.length > 0 && resourceId.length > 0}
      onCancel={onCancel}
      onSubmit={onSubmit}
    >
      <FormField id="callbox-device-group" label="Directory">
        <select
          id="callbox-device-group"
          className={fieldCls}
          value={directoryGroupId}
          onChange={(e) => setDirectoryGroupId(e.target.value)}
        >
          {groups.map((group) => (
            <option key={group.id} value={group.id}>
              {group.name}
            </option>
          ))}
        </select>
      </FormField>
      <FormField id="callbox-device-resource" label="Callbox / reader / door">
        <select
          id="callbox-device-resource"
          className={fieldCls}
          value={resourceId}
          onChange={(e) => onResourceChange(e.target.value)}
          required
        >
          <option value="">Select discovered callbox, doorbell reader, or entry door</option>
          {availableResources.map((resource) => (
            <option key={resource.id} value={resource.id}>
              {resourceOptionLabel(resource)}
            </option>
          ))}
        </select>
        <p className="mt-1 font-body-sm text-body-sm text-on-surface-variant">
          {availableResources.length === 0
            ? 'No eligible callboxes, doorbell readers, or entry doors are available yet. Use Refresh hardware to retry.'
            : selectedResource
              ? `Selected hardware: ${resourceOptionLabel(selectedResource)}`
              : 'Discovered intercoms, readers with doorbell support, and door-backed entry devices are shown.'}
        </p>
      </FormField>
      <FormField id="callbox-device-name" label="Name">
        <input
          id="callbox-device-name"
          className={fieldCls}
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={120}
          required
        />
      </FormField>
      <FormField id="callbox-device-location" label="Location">
        <input
          id="callbox-device-location"
          className={fieldCls}
          value={locationLabel}
          onChange={(e) => setLocationLabel(e.target.value)}
          maxLength={120}
        />
      </FormField>
      <FormField id="callbox-device-placement" label="Placement">
        <select
          id="callbox-device-placement"
          className={fieldCls}
          value={placement}
          onChange={(e) => setPlacement(e.target.value as Placement)}
        >
          <option value="perimeter">Perimeter</option>
          <option value="interior">Interior</option>
        </select>
      </FormField>
      <FormField id="callbox-device-ring" label="Ring target">
        <select
          id="callbox-device-ring"
          className={fieldCls}
          value={ringTargetType}
          onChange={(e) => setRingTargetType(e.target.value as RingTargetType)}
        >
          <option value="admin_office">Admin office</option>
          <option value="tenant">Tenant</option>
          <option value="user">Portal user</option>
          <option value="custom">Custom</option>
          <option value="none">None</option>
        </select>
      </FormField>
      {ringTargetType === 'tenant' ? (
        <FormField id="callbox-device-ring-tenant" label="Tenant">
          <select
            id="callbox-device-ring-tenant"
            className={fieldCls}
            value={ringTenantId}
            onChange={(e) => setRingTenantId(e.target.value)}
            required
          >
            <option value="">Select tenant</option>
            {tenants.map((tenant) => (
              <option key={tenant.id} value={tenant.id}>
                {tenant.name}
              </option>
            ))}
          </select>
        </FormField>
      ) : null}
      {ringTargetType === 'user' ? (
        <FormField id="callbox-device-ring-user" label="Portal user">
          <select
            id="callbox-device-ring-user"
            className={fieldCls}
            value={ringUserId}
            onChange={(e) => setRingUserId(e.target.value)}
            required
          >
            <option value="">Select user</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {userOptionLabel(user)}
              </option>
            ))}
          </select>
        </FormField>
      ) : null}
      <FormField id="callbox-device-ring-label" label="Ring label">
        <input
          id="callbox-device-ring-label"
          className={fieldCls}
          value={ringLabel}
          onChange={(e) => setRingLabel(e.target.value)}
          maxLength={120}
        />
      </FormField>
      <FormField id="callbox-device-ring-phone" label="Ring phone">
        <input
          id="callbox-device-ring-phone"
          className={fieldCls}
          value={ringPhone}
          onChange={(e) => setRingPhone(e.target.value)}
          maxLength={40}
        />
        <p className="mt-1 font-body-sm text-body-sm text-on-surface-variant">
          Save records the target in the portal. Sync routing pushes it toward the callbox, but
          UniFi does not expose an official button-routing API yet — treat routing as
          portal-managed until that contract is verified.
        </p>
      </FormField>
      <FormField id="callbox-device-notes" label="Notes">
        <textarea
          id="callbox-device-notes"
          className={fieldCls}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={2000}
          rows={3}
        />
      </FormField>
      <label className="flex items-center gap-2 font-body-md text-body-md text-on-surface">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        Enabled
      </label>
    </CrudModal>
  );
}

function LinkCallboxModal({
  row,
  groups,
  onCancel,
  onDone,
}: {
  row: CallboxDevice;
  groups: CallboxGroup[];
  onCancel: () => void;
  onDone: (message: string) => void;
}) {
  const [directoryGroupId, setDirectoryGroupId] = useState(row.directory_group_id);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const idemKey = useMemo(() => newIdempotencyKey(), []);
  const selectedGroup = groups.find((group) => group.id === directoryGroupId) ?? null;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const body = UpdateCallboxDeviceBody.parse({ directory_group_id: directoryGroupId });
      await apiClient
        .withIdempotencyKey(idemKey)
        .patch(`/api/v1/admin/callboxes/devices/${row.id}`, body, masterRequest);
      onDone(
        `"${callboxSemanticName(row)}" linked to ${selectedGroup?.name ?? 'the selected set'}.`,
      );
    } catch (err) {
      setError(errorMessage(err));
      setSubmitting(false);
    }
  }

  return (
    <CrudModal
      title={`Link / move ${callboxSemanticName(row)}`}
      icon="hub"
      submitting={submitting}
      error={error}
      submitLabel="Save link"
      canSubmit={directoryGroupId.length > 0 && directoryGroupId !== row.directory_group_id}
      onCancel={onCancel}
      onSubmit={onSubmit}
    >
      <FormField id="callbox-link-group" label="Linked set">
        <select
          id="callbox-link-group"
          className={fieldCls}
          value={directoryGroupId}
          onChange={(e) => setDirectoryGroupId(e.target.value)}
        >
          {groups.map((group) => (
            <option key={group.id} value={group.id}>
              {group.name}
            </option>
          ))}
        </select>
      </FormField>
      <p className="font-body-sm text-body-sm text-on-surface-variant">
        Callboxes in one linked set share the same directory entries.
      </p>
    </CrudModal>
  );
}

function AssignTenantModal({
  row,
  tenants,
  onCancel,
  onDone,
}: {
  row: CallboxDevice;
  tenants: TenantSummary[];
  onCancel: () => void;
  onDone: (message: string) => void;
}) {
  const [tenantId, setTenantId] = useState(tenants[0]?.id ?? '');
  const [canManage, setCanManage] = useState(true);
  const [canManageRing, setCanManageRing] = useState(false);
  const [labelOverride, setLabelOverride] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const idemKey = useMemo(() => newIdempotencyKey(), []);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const body = AssignCallboxTenantBody.parse({
        tenant_id: tenantId,
        can_manage_directory: canManage,
        can_manage_ring_target: canManageRing,
        label_override: nullableText(labelOverride),
      });
      await apiClient
        .withIdempotencyKey(idemKey)
        .post(`/api/v1/admin/callboxes/devices/${row.id}/tenants`, body, masterRequest);
      onDone('Callbox assignment saved.');
    } catch (err) {
      setError(errorMessage(err));
      setSubmitting(false);
    }
  }

  return (
    <CrudModal
      title={`Assign ${row.name}`}
      icon="link"
      submitting={submitting}
      error={error}
      submitLabel="Assign"
      canSubmit={tenantId.length > 0}
      onCancel={onCancel}
      onSubmit={onSubmit}
    >
      <FormField id="callbox-assign-tenant" label="Tenant">
        <select
          id="callbox-assign-tenant"
          className={fieldCls}
          value={tenantId}
          onChange={(e) => setTenantId(e.target.value)}
        >
          {tenants.map((tenant) => (
            <option key={tenant.id} value={tenant.id}>
              {tenant.name}
            </option>
          ))}
        </select>
      </FormField>
      <FormField id="callbox-assign-label" label="Label override">
        <input
          id="callbox-assign-label"
          className={fieldCls}
          value={labelOverride}
          onChange={(e) => setLabelOverride(e.target.value)}
          maxLength={120}
        />
      </FormField>
      <label className="flex items-center gap-2 font-body-md text-body-md text-on-surface">
        <input
          type="checkbox"
          checked={canManage}
          onChange={(e) => setCanManage(e.target.checked)}
        />
        Tenant can manage directory entries
      </label>
      <label className="flex items-center gap-2 font-body-md text-body-md text-on-surface">
        <input
          type="checkbox"
          checked={canManageRing}
          onChange={(e) => setCanManageRing(e.target.checked)}
        />
        Tenant can manage button routing
      </label>
    </CrudModal>
  );
}

function RingTargetModal({
  row,
  users,
  tenantId,
  onCancel,
  onDone,
}: {
  row: CallboxDevice;
  users: UserSummary[];
  tenantId: string | null;
  onCancel: () => void;
  onDone: (message: string) => void;
}) {
  const initialTarget = row.ring_target_type === 'admin_office' ? 'custom' : row.ring_target_type;
  const [ringTargetType, setRingTargetType] = useState<RingTargetType>(initialTarget);
  const [ringUserId, setRingUserId] = useState(row.ring_user_id ?? '');
  const [ringLabel, setRingLabel] = useState(row.ring_label ?? '');
  const [ringPhone, setRingPhone] = useState(row.ring_phone ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const idemKey = useMemo(() => newIdempotencyKey(), []);
  const canSubmit =
    ringTargetType === 'tenant'
      ? Boolean(tenantId)
      : ringTargetType === 'user'
        ? ringUserId.length > 0
        : ringTargetType === 'custom'
          ? ringLabel.trim().length > 0 || ringPhone.trim().length > 0
          : true;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const body = UpdateCallboxRingTargetBody.parse({
        ring_target_type: ringTargetType,
        ring_tenant_id: ringTargetType === 'tenant' ? tenantId : null,
        ring_user_id: ringTargetType === 'user' ? ringUserId || null : null,
        ring_label: nullableText(ringLabel),
        ring_phone: nullableText(ringPhone),
      });
      await apiClient
        .withIdempotencyKey(idemKey)
        .patch(`/api/v1/callbox/devices/${row.id}/ring-target`, body);
      onDone(`Button routing for "${row.name}" updated.`);
    } catch (err) {
      setError(errorMessage(err));
      setSubmitting(false);
    }
  }

  return (
    <CrudModal
      title={`Button routing · ${row.name}`}
      icon="notifications_active"
      submitting={submitting}
      error={error}
      submitLabel="Save"
      canSubmit={canSubmit}
      onCancel={onCancel}
      onSubmit={onSubmit}
    >
      <FormField id="tenant-callbox-device-ring" label="Ring target">
        <select
          id="tenant-callbox-device-ring"
          className={fieldCls}
          value={ringTargetType}
          onChange={(e) => setRingTargetType(e.target.value as RingTargetType)}
        >
          <option value="tenant">Tenant</option>
          <option value="user">Portal user</option>
          <option value="custom">Custom</option>
          <option value="none">None</option>
        </select>
      </FormField>
      {ringTargetType === 'user' ? (
        <FormField id="tenant-callbox-device-ring-user" label="Portal user">
          <select
            id="tenant-callbox-device-ring-user"
            className={fieldCls}
            value={ringUserId}
            onChange={(e) => setRingUserId(e.target.value)}
            required
          >
            <option value="">Select user</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {userOptionLabel(user)}
              </option>
            ))}
          </select>
        </FormField>
      ) : null}
      {ringTargetType === 'custom' ? (
        <>
          <FormField id="tenant-callbox-device-ring-label" label="Ring label">
            <input
              id="tenant-callbox-device-ring-label"
              className={fieldCls}
              value={ringLabel}
              onChange={(e) => setRingLabel(e.target.value)}
              maxLength={120}
            />
          </FormField>
          <FormField id="tenant-callbox-device-ring-phone" label="Ring phone">
            <input
              id="tenant-callbox-device-ring-phone"
              className={fieldCls}
              value={ringPhone}
              onChange={(e) => setRingPhone(e.target.value)}
              maxLength={40}
            />
            <p className="mt-1 font-body-sm text-body-sm text-on-surface-variant">
              Tenant changes update the portal target. Master admins manage hardware routing from
              the callbox row.
            </p>
          </FormField>
        </>
      ) : null}
    </CrudModal>
  );
}

function MemberModal({
  row,
  isMaster,
  groups,
  tenants,
  selectedGroupId,
  onCancel,
  onDone,
}: {
  row: DirectoryMember | null;
  isMaster: boolean;
  groups: CallboxGroup[];
  tenants: TenantSummary[];
  selectedGroupId: string;
  onCancel: () => void;
  onDone: (message: string) => void;
}) {
  const [directoryGroupId, setDirectoryGroupId] = useState(
    row?.directory_group_id ?? selectedGroupId,
  );
  const [tenantId, setTenantId] = useState(row?.tenant_id ?? '');
  const [kind, setKind] = useState<MemberKind>(row?.kind ?? 'tenant');
  const [displayName, setDisplayName] = useState(row?.display_name ?? '');
  const [subtitle, setSubtitle] = useState(row?.subtitle ?? '');
  const [phone, setPhone] = useState(row?.phone ?? '');
  const [visibility, setVisibility] = useState(row?.visibility ?? 'public');
  const [sortOrder, setSortOrder] = useState(String(row?.sort_order ?? 0));
  const [enabled, setEnabled] = useState(row?.enabled ?? true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const idemKey = useMemo(() => newIdempotencyKey(), []);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        directory_group_id: directoryGroupId,
        tenant_id: isMaster ? tenantId || null : undefined,
        kind,
        display_name: displayName.trim(),
        subtitle: nullableText(subtitle),
        phone: nullableText(phone),
        visibility,
        target_tenant_id: isMaster && kind === 'tenant' ? tenantId || null : undefined,
        sort_order: Number(sortOrder) || 0,
        enabled,
      };
      if (row) {
        const body = UpdateCallboxDirectoryMemberBody.parse(payload);
        const url = isMaster
          ? `/api/v1/admin/callboxes/directory/${row.id}`
          : `/api/v1/callbox/members/${row.id}`;
        await apiClient
          .withIdempotencyKey(idemKey)
          .patch(url, body, isMaster ? masterRequest : undefined);
        onDone(`Entry "${body.display_name ?? row.display_name}" updated.`);
      } else {
        const body = CreateCallboxDirectoryMemberBody.parse(payload);
        const url = isMaster ? '/api/v1/admin/callboxes/directory' : '/api/v1/callbox/members';
        await apiClient
          .withIdempotencyKey(idemKey)
          .post(url, body, isMaster ? masterRequest : undefined);
        onDone(`Entry "${body.display_name}" created.`);
      }
    } catch (err) {
      setError(errorMessage(err));
      setSubmitting(false);
    }
  }

  return (
    <CrudModal
      title={row ? `Edit ${row.display_name}` : 'Add entry'}
      icon={row ? 'edit' : 'add'}
      submitting={submitting}
      error={error}
      submitLabel={row ? 'Save' : 'Create'}
      canSubmit={displayName.trim().length > 0 && directoryGroupId.length > 0}
      onCancel={onCancel}
      onSubmit={onSubmit}
    >
      <FormField id="callbox-member-group" label="Directory">
        <select
          id="callbox-member-group"
          className={fieldCls}
          value={directoryGroupId}
          onChange={(e) => setDirectoryGroupId(e.target.value)}
        >
          {groups.map((group) => (
            <option key={group.id} value={group.id}>
              {group.name}
            </option>
          ))}
        </select>
      </FormField>
      {isMaster ? (
        <FormField id="callbox-member-tenant" label="Tenant">
          <select
            id="callbox-member-tenant"
            className={fieldCls}
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
          >
            <option value="">Building / admin</option>
            {tenants.map((tenant) => (
              <option key={tenant.id} value={tenant.id}>
                {tenant.name}
              </option>
            ))}
          </select>
        </FormField>
      ) : null}
      <FormField id="callbox-member-kind" label="Type">
        <select
          id="callbox-member-kind"
          className={fieldCls}
          value={kind}
          onChange={(e) => setKind(e.target.value as MemberKind)}
        >
          <option value="tenant">Tenant</option>
          {isMaster ? <option value="admin_office">Admin office</option> : null}
          {isMaster ? <option value="household">Household</option> : null}
          <option value="custom">Custom</option>
        </select>
      </FormField>
      <FormField id="callbox-member-name" label="Display name">
        <input
          id="callbox-member-name"
          className={fieldCls}
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          maxLength={120}
          required
        />
      </FormField>
      <FormField id="callbox-member-subtitle" label="Subtitle">
        <input
          id="callbox-member-subtitle"
          className={fieldCls}
          value={subtitle}
          onChange={(e) => setSubtitle(e.target.value)}
          maxLength={120}
        />
      </FormField>
      <FormField id="callbox-member-phone" label="Phone">
        <input
          id="callbox-member-phone"
          className={fieldCls}
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          maxLength={40}
        />
      </FormField>
      <FormField id="callbox-member-visibility" label="Visibility">
        <select
          id="callbox-member-visibility"
          className={fieldCls}
          value={visibility}
          onChange={(e) => setVisibility(e.target.value)}
        >
          <option value="public">Public</option>
          <option value="private">Private</option>
        </select>
      </FormField>
      <FormField id="callbox-member-sort" label="Sort order">
        <input
          id="callbox-member-sort"
          className={fieldCls}
          type="number"
          min={0}
          max={10000}
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
        />
      </FormField>
      <label className="flex items-center gap-2 font-body-md text-body-md text-on-surface">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        Enabled
      </label>
    </CrudModal>
  );
}

function DeleteModal({
  title,
  body,
  submitLabel,
  onCancel,
  onSubmit,
}: {
  title: string;
  body: string;
  submitLabel: string;
  onCancel: () => void;
  onSubmit: () => Promise<void>;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit();
    } catch (err) {
      setError(errorMessage(err));
      setSubmitting(false);
    }
  }

  return (
    <CrudModal
      title={title}
      icon="delete"
      tone="destructive"
      submitting={submitting}
      error={error}
      submitLabel={submitLabel}
      canSubmit
      onCancel={onCancel}
      onSubmit={submit}
    >
      <p className="font-body-md text-body-md text-on-surface">{body}</p>
    </CrudModal>
  );
}
